import { useCallback } from 'react'
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form'

const fcSolutionValidate = (input?: string): boolean => {
  if (typeof window !== 'undefined' && window.automatedTestsSkipCaptcha) {
    return true
  }

  return Boolean(input)
}

type Parameters<T extends FieldValues> = {
  fieldName: Path<T>
  register: UseFormRegister<T>
  setValue: UseFormSetValue<T>
  watch: UseFormWatch<T>
}

type CallbackType<T extends FieldValues> = {
  onCaptchaDone: (fcSolution: PathValue<T, Path<T>>) => void
  watchFriendlyCaptchaChange: UseFormWatch<T>
}

export const useHookFormFriendlyCaptchaFieldControls = <T extends FieldValues>({
  fieldName,
  register,
  setValue,
  watch,
}: Parameters<T>): CallbackType<T> => {
  register(fieldName, { validate: fcSolutionValidate })

  const onCaptchaDone = useCallback(
    (fcSolution: PathValue<T, Path<T>>): void => {
      // https://github.com/orgs/react-hook-form/discussions/7246
      setValue(fieldName, fcSolution, { shouldValidate: true })
    },
    [setValue, fieldName],
  )

  const watchFriendlyCaptchaChange = watch(fieldName)

  return {
    onCaptchaDone,
    watchFriendlyCaptchaChange,
  }
}
